import React, { useState, useRef, useEffect } from 'react';
import logo from '../../logo.jpg';
import { Colors as C } from '../../constants/Colors.js';
import { supabase, signOutApp } from '../../lib/supabase.js';
import { resolveProfileAvatarUrl } from '../../lib/profileAvatar.js';
import Icon from './Icon.js';
import PartnerVerifiedBadge from './PartnerVerifiedBadge.js';
import { isDreamvinylVerifiedPartner } from '../utils/partnerVerified.js';
import { profileLevelLabel } from '../profileConstants.js';
import { profileGroovesBalance } from '../constants/groovesEconomy.js';
import { adminMenuBadgeTotal, fetchAdminMenuBadgeCounts, ADMIN_ALERT_DOT_STYLE } from '../../lib/adminMenuBadges.js';
import { useBreakpoints } from '../hooks/useBreakpoints.js';

/** Altura sticky de WebTopBar (drawer de filtros, backdrops). */
export function getWebTopBarStickyTopPx({ compact = false, mobile = false, mini = false, sectionNav = false } = {}) {
  if (mini) return 56;
  if (mobile && sectionNav) return compact ? 96 : 104;
  return compact ? 56 : 80;
}

const SECTION_NAV_ITEMS = [
  { key: 'marketplace', label: 'Mercado' },
  { key: 'subastas', label: 'Subastas' },
  { key: 'comunidad', label: 'Comunidad' },
  { key: 'noticias', label: 'Noticias' },
];

function UserDropdown({ user, onNavigate, onShowModal, onSignOut, dark, compactMobile = false }) {
  const [open, setOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileMeta, setProfileMeta] = useState(null);
  const [groovesSaldo, setGroovesSaldo] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setAvatarUrl('');
      setProfileMeta(null);
      setGroovesSaldo(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('avatar_url, username, display_name, level, is_verified, is_store, partner_active_roles, grooves, grooves_total')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setAvatarUrl(resolveProfileAvatarUrl(data?.avatar_url) || '');
        setProfileMeta(data || null);
        const g = data != null ? profileGroovesBalance(data) : 0;
        setGroovesSaldo(Number.isFinite(g) ? g : 0);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const go = (page, navOpts) => {
    setOpen(false);
    onNavigate(page, navOpts);
  };

  const menuItems = [
    { icon: 'user',    label: 'Mi perfil',      page: 'perfil'      },
    { icon: 'bag',     label: 'Mis ventas',      page: 'misventas'   },
    { icon: 'bag',     label: 'Mis compras',     page: 'pedido'      },
    { icon: 'heart',   label: 'Favoritos',       page: 'marketplace', navOptions: { favoritesOnly: true } },
    { icon: 'chat',    label: 'Mensajes',        page: 'chat'        },
    { icon: 'bell',    label: 'Notificaciones',  page: 'notificaciones' },
    { icon: 'store',   label: 'Mi partner',       page: 'registro-tienda', navOptions: { partnerEntry: true } },
    null,
    { icon: 'settings', label: 'Ajustes',         page: 'ajustes'     },
    { icon: 'logout',  label: 'Cerrar sesión',   page: null, danger: true },
  ];

  if (!user) {
    if (compactMobile) {
      return (
        <button
          type="button"
          onClick={() => onShowModal && onShowModal('login')}
          style={{
            background: C.gold,
            color: '#0b0b10',
            border: 'none',
            padding: '8px 12px',
            borderRadius: 7,
            fontWeight: 800,
            fontSize: '0.76rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Entrar
        </button>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => onShowModal && onShowModal('login')}
          style={{ background: 'transparent', border: `1px solid ${dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)'}`, color: dark ? 'rgba(255,255,255,0.7)' : '#444', padding: '8px 14px', borderRadius: 7, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
          Iniciar sesión
        </button>
        <button onClick={() => onShowModal && onShowModal('register')}
          style={{ background: '#1a1a2e', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 7, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
          Registrarse
        </button>
      </div>
    );
  }

  const groovesDisplay = groovesSaldo == null ? '…' : String(Math.max(0, groovesSaldo));

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        title="Grooves — ver en Mi perfil"
        onClick={() => {
          setOpen(false);
          onNavigate && onNavigate('perfil');
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compactMobile ? 0 : 5,
          padding: compactMobile ? '6px 8px' : '6px 10px',
          borderRadius: 10,
          border: `1px solid rgba(200,168,75,0.28)`,
          background: dark ? 'rgba(200,168,75,0.08)' : 'rgba(200,168,75,0.06)',
          cursor: 'pointer',
          flexShrink: 0,
        }}>
        <Icon name="disc" size={14} color={C.gold} />
        <span style={{ fontSize: '0.78rem', fontWeight: 800, color: dark ? C.gold : '#5c4d1a', letterSpacing: '0.02em', minWidth: 14, textAlign: 'right' }}>{groovesDisplay}</span>
      </button>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: 37, height: 37, borderRadius: '50%', background: 'rgba(200,168,75,0.12)', border: `1.5px solid ${open ? C.gold : 'rgba(200,168,75,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'border-color .15s', position: 'relative' }}>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        ) : (
          <Icon name="user" size={16} color={C.gold}/>
        )}
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', background: C.green, border: `2px solid ${dark ? C.dark : '#fff'}` }}/>
      </button>

      {open && (
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 220, background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.14)', border: '1px solid rgba(0,0,0,0.08)', zIndex: 500, overflow: 'hidden', animation: 'slideInDown .15s ease' }}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(0,0,0,0.07)', background: 'linear-gradient(135deg,#1a1a2e,#0d1020)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(200,168,75,0.15)', border: `1.5px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: C.gold, flexShrink: 0, overflow: 'hidden' }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  user.email?.[0]?.toUpperCase() || 'U'
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {profileMeta?.display_name?.trim() ||
                    profileMeta?.username ||
                    user.user_metadata?.username ||
                    user.email?.split('@')[0] ||
                    'Usuario'}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {profileMeta?.username ? `@${profileMeta.username}` : user.email}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: 'rgba(200,168,75,0.15)', color: C.gold }}>
                {profileLevelLabel(profileMeta?.level ?? 1)} · Nv.{Math.min(5, Math.max(1, Number(profileMeta?.level) || 1))}
              </span>
              {isDreamvinylVerifiedPartner({
                isVerified: profileMeta?.is_verified,
                isStore: profileMeta?.is_store,
                partnerRoleKeys: profileMeta?.partner_active_roles,
              }) ? (
                <PartnerVerifiedBadge size={16} />
              ) : null}
            </div>
          </div>

          <div style={{ padding: '6px 0' }}>
            {menuItems.map((item, i) => {
              if (!item) return <div key={i} style={{ height: 1, background: 'rgba(0,0,0,0.06)', margin: '4px 0' }}/>;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={async () => {
                    if (item.page) {
                      go(item.page, item.navOptions);
                      return;
                    }
                    setOpen(false);
                    const { error } = await signOutApp();
                    if (error) console.warn('Cerrar sesión:', error.message);
                    if (typeof onSignOut === 'function') await onSignOut();
                  }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: '0.84rem', fontWeight: 500, color: item.danger ? C.danger : '#333', transition: 'background .12s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = item.danger ? 'rgba(226,75,74,0.05)' : 'rgba(0,0,0,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <Icon name={item.icon} size={15} color={item.danger ? C.danger : '#888'}/>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WebTopBar({
  onBack,
  title,
  dark,
  user,
  onNavigate,
  navActive,
  mini,
  onShowModal,
  onSignOut,
  notifCount = 0,
  showWelcomeTab = false,
  onWelcomeClick,
  /** Mercado: cabecera más baja al hacer scroll en la home */
  headerCompact = false,
  /** Ocultar pestañas del menú principal (p. ej. `['subastas']` en fase L1). */
  hiddenNavKeys = [],
  /** Mercado: lupa que abre panel de filtros — `{ active, filterCount, onToggle }` */
  marketplaceSearch = null,
  /** En modo claro, color de fondo de la barra (p. ej. mercado unificado con `C.cream`). */
  navBackgroundLight = null,
}) {
  const { isMobile } = useBreakpoints();
  const bg = dark ? C.dark : (navBackgroundLight || '#fff');
  const border = dark ? '0.5px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.09)';
  const navInactiveColor = dark ? 'rgba(255,255,255,0.45)' : '#666';
  const navAdminInactiveColor = dark ? 'rgba(255,255,255,0.45)' : '#888';
  const [unreadCount, setUnreadCount] = useState(notifCount);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [adminPendingTotal, setAdminPendingTotal] = useState(0);

  const logoHeight = mini ? 40 : headerCompact ? 44 : 72;
  const barMinHeight = mini ? 56 : headerCompact ? 56 : 80;
  /** Ancho del botón «Bienvenida» para alinear pestañas Mercado/Subastas/Comunidad/Noticias. */
  const welcomeSlotWidth = headerCompact ? 108 : 124;
  const isMainSectionNav = !!navActive && !mini;
  const showMobileSectionNav = isMobile && isMainSectionNav;
  const stickyTopPx = getWebTopBarStickyTopPx({
    compact: headerCompact,
    mobile: isMobile,
    mini,
    sectionNav: isMainSectionNav,
  });
  const effectiveLogoHeight = mini ? 40 : isMobile ? (headerCompact ? 40 : 48) : logoHeight;
  const rowMinHeight = mini ? 56 : showMobileSectionNav ? (headerCompact ? 52 : 56) : barMinHeight;
  const navItems = SECTION_NAV_ITEMS.filter(({ key }) => !hiddenNavKeys.includes(key));

  const renderSectionNavButton = ({ key, label }, mobileRow = false) => {
    const act = navActive === key;
    const pad = mobileRow
      ? '6px 10px'
      : headerCompact
        ? '6px 12px'
        : '8px 18px';
    const fs = mobileRow ? '0.74rem' : headerCompact ? '0.76rem' : '0.82rem';
    return (
      <button
        key={key}
        type="button"
        className="nav-link-hover"
        style={{
          background: 'none',
          border: 'none',
          padding: pad,
          borderRadius: 6,
          fontSize: fs,
          fontWeight: act ? 700 : 500,
          color: act ? C.gold : navInactiveColor,
          cursor: 'pointer',
          letterSpacing: '0.02em',
          transition: 'all .15s',
          borderBottom: act ? `2px solid ${C.gold}` : '2px solid transparent',
          flexShrink: mobileRow ? 0 : undefined,
          whiteSpace: mobileRow ? 'nowrap' : undefined,
        }}
        onClick={() => onNavigate && onNavigate(key)}
      >
        {label}
      </button>
    );
  };

  useEffect(() => {
    if (!user?.id) {
      setUnreadCount(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      if (!cancelled) setUnreadCount(Number(count ?? 0));
    };
    load();
    const ch = supabase
      .channel(`notif-count:${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, () => {
        load();
      })
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(ch);
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        if (!cancelled) setIsAdminUser(false);
        return;
      }
      const { data } = await supabase.from('profiles').select('is_admin').eq('id', user.id).maybeSingle();
      if (!cancelled) setIsAdminUser(!!data?.is_admin);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !isAdminUser) {
      setAdminPendingTotal(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const badges = await fetchAdminMenuBadgeCounts(user.id);
        if (!cancelled) setAdminPendingTotal(adminMenuBadgeTotal(badges));
      } catch {
        if (!cancelled) setAdminPendingTotal(0);
      }
    };
    load();
    const t = setInterval(load, 90_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [user?.id, isAdminUser]);

  return (
    <nav style={{ background: bg, borderBottom: border, position: 'sticky', top: 0, zIndex: 200, minHeight: stickyTopPx, transition: 'min-height 0.2s ease', width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
      <div style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: isMobile ? '4px 12px' : '4px 20px',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        minHeight: stickyTopPx,
        display: 'flex',
        flexDirection: showMobileSectionNav ? 'column' : 'row',
        alignItems: showMobileSectionNav ? 'stretch' : 'center',
        gap: showMobileSectionNav ? 0 : headerCompact ? 6 : 10,
        transition: 'min-height 0.2s ease',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 6 : headerCompact ? 6 : 10,
          width: '100%',
          minWidth: 0,
          minHeight: showMobileSectionNav ? rowMinHeight : undefined,
        }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: dark ? 'rgba(255,255,255,0.6)' : '#666', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', fontWeight: 500, padding: '6px 8px', borderRadius: 6, flexShrink: 0 }}>
            <Icon name="back" size={15} color={dark ? 'rgba(255,255,255,0.6)' : '#666'}/> {!isMobile ? 'Volver' : null}
          </button>
        )}

        <button onClick={() => onNavigate && onNavigate('marketplace')}
          style={{ border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0, padding: '0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={logo}
            alt="DreamVinyl"
            style={{
              height: effectiveLogoHeight,
              width: 'auto',
              borderRadius: headerCompact || isMobile ? 8 : 10,
              objectFit: 'cover',
              border: `1px solid rgba(201, 162, 39, 0.32)`,
              display: 'block',
              transition: 'height 0.2s ease, border-radius 0.2s ease',
            }}
          />
        </button>

        {!isMobile && !mini && showWelcomeTab ? (
          <button
            type="button"
            onClick={() => onWelcomeClick && onWelcomeClick()}
            style={{
              background: C.gold,
              border: 'none',
              borderRadius: headerCompact ? 6 : 8,
              padding: headerCompact ? '5px 10px' : '7px 14px',
              fontSize: headerCompact ? '0.58rem' : '0.64rem',
              fontWeight: 900,
              color: '#0b0b10',
              letterSpacing: '0.1em',
              boxShadow: '0 2px 10px rgba(201, 162, 39, 0.45)',
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: welcomeSlotWidth,
              transition: 'padding 0.2s ease, font-size 0.2s ease, border-radius 0.2s ease, transform 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
          >
            ▼ Bienvenida
          </button>
        ) : null}
        {!isMobile && !mini && !showWelcomeTab && isMainSectionNav ? (
          <div aria-hidden="true" style={{ width: welcomeSlotWidth, flexShrink: 0 }} />
        ) : null}

        {!mini && !isMobile && (
          <div style={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'center', minWidth: 0 }}>
            {navItems.map(item => renderSectionNavButton(item))}
            {isAdminUser && user?.id ? (
              <button
                type="button"
                className="nav-link-hover"
                onClick={() => onNavigate && onNavigate('admin')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px 14px',
                  borderRadius: 6,
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  color: navActive === 'admin' ? C.gold : navAdminInactiveColor,
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                  borderBottom: navActive === 'admin' ? `2px solid ${C.gold}` : '2px solid transparent',
                  position: 'relative',
                }}
                title={adminPendingTotal > 0 ? `${adminPendingTotal} aviso(s) pendiente(s) en administración` : 'Panel de administración'}
              >
                Admin
                {adminPendingTotal > 0 ? (
                  <span
                    aria-hidden
                    style={{
                      ...ADMIN_ALERT_DOT_STYLE,
                      position: 'absolute',
                      top: 4,
                      right: 2,
                      width: 9,
                      height: 9,
                    }}
                  />
                ) : null}
              </button>
            ) : null}
          </div>
        )}
        {mini && <span style={{ flex: 1, fontSize: '1rem', fontWeight: 600, color: dark ? '#fff' : '#1a1a1a', marginLeft: 8, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>}

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, marginLeft: mini ? 0 : 'auto', flexShrink: 0 }}>
          {marketplaceSearch && !mini ? (
            <button
              type="button"
              data-guest-safe
              title="Buscar y filtros del mercado"
              aria-expanded={marketplaceSearch.active}
              aria-label={marketplaceSearch.active ? 'Cerrar filtros del mercado' : 'Abrir búsqueda y filtros del mercado'}
              onClick={() => marketplaceSearch.onToggle && marketplaceSearch.onToggle()}
              style={{
                position: 'relative',
                width: headerCompact ? 36 : 38,
                height: headerCompact ? 36 : 38,
                borderRadius: '50%',
                border: `1px solid ${marketplaceSearch.active ? C.gold : 'rgba(201, 162, 39, 0.28)'}`,
                background: marketplaceSearch.active ? 'rgba(201, 162, 39, 0.2)' : 'rgba(201, 162, 39, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
            >
              <Icon name="search" size={headerCompact ? 15 : 16} color={marketplaceSearch.active ? '#5c4d1a' : C.gold} />
              {marketplaceSearch.filterCount > 0 ? (
                <span
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: C.gold,
                    color: '#0a0a0f',
                    fontSize: 9,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1.5px solid ${dark ? C.dark : '#fff'}`,
                  }}
                >
                  {marketplaceSearch.filterCount > 9 ? '9+' : marketplaceSearch.filterCount}
                </span>
              ) : null}
            </button>
          ) : null}
          <button onClick={() => { if (user) { onNavigate && onNavigate('notificaciones'); } else { onShowModal && onShowModal('login'); } }}
            style={{ background: 'rgba(201, 162, 39, 0.1)', border: `1px solid rgba(201, 162, 39, 0.28)`, borderRadius: '50%', width: 37, height: 37, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, position: 'relative' }}>
            <Icon name="bell" size={15} color={C.gold}/>
            {user && unreadCount > 0 && <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: C.gold, border: `1.5px solid ${dark ? C.dark : '#fff'}` }}/>}
            {!user && <span style={{ position: 'absolute', top: 2, right: 2, width: 9, height: 9, borderRadius: '50%', background: '#888', border: `1.5px solid ${dark ? C.dark : '#fff'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="5" height="5" viewBox="0 0 12 12" fill="none"><rect x="3" y="5" width="6" height="6" rx="1" stroke="#fff" strokeWidth="1.5"/><path d="M4 5V4a2 2 0 014 0v1" stroke="#fff" strokeWidth="1.5"/></svg></span>}
          </button>

          <UserDropdown user={user} onNavigate={onNavigate} onShowModal={onShowModal} onSignOut={onSignOut} dark={dark} compactMobile={isMobile && !user}/>

          <button
            onClick={() => {
              if (!user) {
                onShowModal && onShowModal('register');
                return;
              }
              onNavigate && onNavigate('publicar');
            }}
            style={{ background: C.gold, color: '#0b0b10', border: 'none', padding: isMobile ? '8px 10px' : headerCompact ? '8px 14px' : '10px 18px', borderRadius: 8, fontWeight: 800, fontSize: isMobile ? '0.72rem' : headerCompact ? '0.76rem' : '0.82rem', cursor: 'pointer', letterSpacing: '0.02em', boxShadow: '0 2px 10px rgba(201, 162, 39, 0.35)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            + VENDER
          </button>
        </div>
        </div>

        {showMobileSectionNav ? (
          <div
            style={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              borderTop: dark ? '0.5px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
              padding: '2px 0 4px',
              margin: '0 -12px',
              paddingLeft: 12,
              paddingRight: 12,
            }}
          >
            {navItems.map(item => renderSectionNavButton(item, true))}
            {isAdminUser && user?.id ? (
              <button
                type="button"
                className="nav-link-hover"
                onClick={() => onNavigate && onNavigate('admin')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: 6,
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  color: navActive === 'admin' ? C.gold : navAdminInactiveColor,
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                  borderBottom: navActive === 'admin' ? `2px solid ${C.gold}` : '2px solid transparent',
                  position: 'relative',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
                title={adminPendingTotal > 0 ? `${adminPendingTotal} aviso(s) pendiente(s) en administración` : 'Panel de administración'}
              >
                Admin
                {adminPendingTotal > 0 ? (
                  <span
                    aria-hidden
                    style={{
                      ...ADMIN_ALERT_DOT_STYLE,
                      position: 'absolute',
                      top: 4,
                      right: 2,
                      width: 9,
                      height: 9,
                    }}
                  />
                ) : null}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </nav>
  );
}
